"use client";

import clsx from "clsx";
import type React from "react";
import Button from "src/components/common/Button/Button";
import Divider from "src/components/common/Divider/Divider";
import IconTile, { type IconTileProps } from "src/components/common/IconTile/IconTile";
import type { LinkHref } from "src/components/common/Link/Link";
import Popover, { type PopoverContentProps } from "src/components/common/Popover/Popover";
import { usePopover } from "src/components/common/Popover/PopoverContext";
import Typography from "src/components/common/Typography/Typography";
import styles from "./FloatingPanel.module.scss";

/**
 * Panel width, capped by the viewport: `compact` for a short list of options, `default` for menus,
 * `wide` for feeds, `extraWide` for forms such as filters.
 */
export type FloatingPanelWidth = "compact" | "default" | "wide" | "extraWide";

export interface FloatingPanelProps extends React.HTMLProps<HTMLDivElement> {
  width?: FloatingPanelWidth;
}

const FloatingPanel = (props: FloatingPanelProps) => {
  const { className, width = "default", ...otherProps } = props;
  return (
    <div
      data-floating-panel={true}
      data-width={width}
      className={clsx(styles.content, className)}
      {...otherProps}
    />
  );
};

export type FloatingPanelContentProps = PopoverContentProps & {
  width?: FloatingPanelWidth;
};

const FloatingPanelContent = (props: FloatingPanelContentProps) => {
  const { className, children, width = "default", ...otherProps } = props;

  return (
    <Popover.Content
      data-floating-panel={true}
      data-width={width}
      className={clsx(styles.content, className)}
      {...otherProps}
    >
      {children}
    </Popover.Content>
  );
};

const FloatingPanelHeader = (props: React.HTMLProps<HTMLDivElement>) => {
  const { className, ...otherProps } = props;
  return <div className={clsx(styles.header, className)} {...otherProps} />;
};

/** The header's leading icon: a small, soft primary `IconTile`. */
const FloatingPanelHeaderIcon = (props: Omit<IconTileProps, "size" | "color" | "fill">) => (
  <IconTile size="sm" customColor="primary" fill="soft" {...props} />
);

const FloatingPanelTitle = (props: React.ComponentPropsWithoutRef<typeof Typography>) => {
  const { className, ...otherProps } = props;
  return (
    <Typography
      as="h2"
      variant="heading4"
      className={clsx(styles.title, className)}
      {...otherProps}
    />
  );
};

const FloatingPanelDescription = (props: React.ComponentPropsWithoutRef<typeof Typography>) => {
  const { className, ...otherProps } = props;
  return (
    <Typography
      as="p"
      variant="bodyExtraSmall"
      tone="muted"
      className={clsx(styles.description, className)}
      {...otherProps}
    />
  );
};

const FloatingPanelBody = (props: React.HTMLProps<HTMLDivElement>) => {
  const { className, ...otherProps } = props;
  return <div className={clsx(styles.body, className)} {...otherProps} />;
};

export interface FloatingPanelSectionProps extends React.HTMLProps<HTMLElement> {
  /** `default` insets actions from the panel edge; `form` fits fields; `none` for a full-bleed list. */
  padding?: "default" | "form" | "none";
}

const FloatingPanelSection = (props: FloatingPanelSectionProps) => {
  const { className, padding = "default", ...otherProps } = props;
  return (
    <section data-padding={padding} className={clsx(styles.section, className)} {...otherProps} />
  );
};

const FloatingPanelFooter = (props: React.HTMLProps<HTMLDivElement>) => {
  const { className, ...otherProps } = props;
  return <div className={clsx(styles.footer, className)} {...otherProps} />;
};

export interface FloatingPanelActionProps {
  className?: string;
  description?: string;
  disabled?: boolean;
  dismiss?: boolean;
  href?: LinkHref;
  icon?: React.ReactNode;
  label: string;
  meta?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLElement>;
  selected?: boolean;
  color?: "neutral" | "danger";
}

const FloatingPanelAction = (props: FloatingPanelActionProps) => {
  const {
    className,
    description,
    disabled,
    dismiss = true,
    href,
    icon,
    label,
    meta,
    onClick,
    selected,
    color = "neutral",
  } = props;
  const popover = usePopover();

  return (
    <Button
      label={label}
      href={href as any}
      disabled={disabled}
      variant="bare"
      color={color}
      ripple={false}
      data-selected={selected ? "true" : undefined}
      data-color={color}
      className={clsx(styles.action, className)}
      onClick={(event: React.MouseEvent<HTMLElement>) => {
        onClick?.(event);
        if (dismiss && !event.defaultPrevented) {
          popover?.setVisible(false);
        }
      }}
    >
      {icon ? <span className={styles.actionIcon}>{icon}</span> : null}
      <span className={styles.actionText}>
        <Typography as="span" variant="bodySmall" weight="medium">
          {label}
        </Typography>
        {description ? (
          <Typography as="span" variant="bodyExtraSmall" tone="muted">
            {description}
          </Typography>
        ) : null}
      </span>
      {meta ? <span className={styles.actionMeta}>{meta}</span> : null}
    </Button>
  );
};

const FloatingPanelDivider = (props: React.ComponentPropsWithoutRef<typeof Divider>) => {
  const { className, ...otherProps } = props;
  return <Divider className={clsx(styles.divider, className)} {...otherProps} />;
};

FloatingPanel.Action = FloatingPanelAction;
FloatingPanel.Body = FloatingPanelBody;
FloatingPanel.Content = FloatingPanelContent;
FloatingPanel.Description = FloatingPanelDescription;
FloatingPanel.Divider = FloatingPanelDivider;
FloatingPanel.Footer = FloatingPanelFooter;
FloatingPanel.Header = FloatingPanelHeader;
FloatingPanel.HeaderIcon = FloatingPanelHeaderIcon;
FloatingPanel.Section = FloatingPanelSection;
FloatingPanel.Title = FloatingPanelTitle;

export default FloatingPanel;
